import React, { CSSProperties, useImperativeHandle, useMemo, useRef, forwardRef } from "react";

import { SemanticICONS } from "semantic-ui-react";

import moment from "moment";

// UX
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";

export type TimeComboBoxNewProps = {
  id?: string;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
  value?: string;
  icon?: SemanticICONS;
  interval?: number;
  maxHour?: number;
  minHour?: number;
  placeholder?: string;
  disabled?: boolean;
  fluid?: boolean;
  noMinWidth?: boolean;
  onTextChange?: (time: string) => void;
};

const IconStyle = { top: "0.7rem" };

const TimeComboBoxNew = forwardRef<any, TimeComboBoxNewProps>((props, ref) => {
  const {
    id,
    value = "",
    disabled,
    fluid: isFluid,
    icon = "time",
    inputStyle,
    interval = 30,
    maxHour = 30,
    minHour = 0,
    noMinWidth,
    placeholder = "Choose Time",
    style,
    onTextChange,
  } = props;

  const inputRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    inputRef,
  }));

  const timeOptions = useMemo(() => {
    const generated: string[] = [];
    const date = moment().format("MM/DD/YYYY");

    const paddedMinHour = `0${minHour}`.slice(-2);
    const paddedMaxHour = `0${maxHour}`.slice(-2);

    let start = moment(`${date} ${paddedMinHour}:00`, "MM/DD/YYYY HH:mm");
    let end = moment(`${date} ${paddedMaxHour}:00`, "MM/DD/YYYY HH:mm");

    if (maxHour === 23) {
      end = moment(`${date} ${paddedMaxHour}:59`, "MM/DD/YYYY HH:mm");
    } else if (maxHour > 23) {
      const remainHours = maxHour - 23;
      const paddedRemain = `0${remainHours}`.slice(-2);
      end = moment(`${date} ${paddedRemain}:00`, "MM/DD/YYYY HH:mm").add(1, "days");
    }

    while (start <= end) {
      generated.push(moment(start).format("HH:mm"));
      start = moment(start).add(interval, "minutes");
    }

    if (value && !generated.includes(value)) {
      generated.unshift(value);
    }

    return generated.map((t, i) => ({
      key: i + 1,
      text: t,
      value: t,
    }));
  }, [value, minHour, maxHour, interval]);

  const handleChange = (_e: any, { value }: any) => {
    if (value !== props.value) {
      const foundOption = timeOptions.find((option) => option.value === value);

      onTextChange?.(foundOption?.text || value);
    }
  };

  return (
    <DropdownOptions
      ref={inputRef}
      id={id}
      disabled={disabled}
      fluid={isFluid}
      icon={icon}
      iconStyle={IconStyle}
      inputStyle={inputStyle}
      noMinWidth={noMinWidth}
      placeholder={placeholder}
      style={style}
      value={value || ""}
      formatTimeOnBlur
      search
      options={timeOptions}
      onChange={handleChange}
    />
  );
});

TimeComboBoxNew.displayName = "TimeComboBoxNew";

export default React.memo(TimeComboBoxNew);
